import {html, TemplateResult} from "lit";
import {keyed} from "lit/directives/keyed.js";
import {translate} from "lit-translate";
import {renderColorSample} from "../../../helpers/header-item-style.helper";

/** What a dialog hands in to show the text colour field and the sample (feature plan 0074). */
export interface TextColorFieldOptions {
    /** The bound form instance — the key of `keyed`: a new form is a new field, so the remembered fixed colour never
     *  carries over to another record. Not the uid: advanced-form and the project dialog get the new form only
     *  asynchronously, a uid key would meet the old form in between. A reset builds a new form only in the two
     *  `@ha/formhandler` dialogs; in advanced-form the form stays, and switching "Automatisch" off after a reset may
     *  offer the discarded colour again — only as the starting value, nothing is saved (review 0074-02, accepted). */
    readonly form: object;
    /** The form binding of `textColor` (`bindTo('textColor')` of either form system). */
    readonly binding: unknown;
    /** The current values from the form. */
    readonly color: string | null;
    readonly textColor: string | null;
    /** The record's name as the sample shows it. */
    readonly sampleText: string;
    /** Called on every change of the field — the dialog re-renders and reads the form again. */
    readonly onChange: () => void;
}

/**
 * The "Schriftfarbe" field (`ha-input-color nullable`, "Automatisch" = null) and below it the sample of the
 * combination — the same in all six dialogs with a record colour (employee, issue type, status, label, project,
 * daily). The sample only shows; it neither rates the contrast nor blocks saving (Anke's decision 4).
 */
export function renderTextColorField(options: TextColorFieldOptions): TemplateResult {
    return html`
        ${keyed(options.form, html`
            <ha-input-color class="max9999-flexrow" data-type="text-color"
                            nullable
                            label=${translate('components.ha-dialog.text-color')}
                            .autoAgainst=${options.color}
                            ${options.binding}
                            @value-changed=${options.onChange}
            ></ha-input-color>
        `)}
        <div class="color-sample-row" data-type="color-sample">
            ${renderColorSample(options.color, options.textColor, options.sampleText)}
        </div>
    `;
}

/** A form of `@ha/formhandler` as far as the text colour field needs it. */
interface FormhandlerForm {
    getField(key: string): {getValue(): unknown} | null | undefined;
}

/**
 * The same for the two `@ha/formhandler` dialogs (project, daily): colour, text colour and the sample text are read
 * from the form's `color`, `textColor` and `title` fields.
 */
export function renderTextColorFieldOfForm(form: FormhandlerForm, binding: unknown, onChange: () => void): TemplateResult {
    const value = (key: string): unknown => form.getField(key)?.getValue();
    return renderTextColorField({
        form,
        binding,
        color: (value(`color`) as string | null | undefined) ?? null,
        textColor: (value(`textColor`) as string | null | undefined) ?? null,
        sampleText: (value(`title`) as string | null | undefined) ?? ``,
        onChange,
    });
}
